/* eslint-disable no-console */
import {Provider} from 'domain/usecases/private/authorizations/get-providers-list';
import {useExternalErrorMessageStore} from 'presentation/components/UploadFiles/store/useExternalErrorMessageStore';
import {useUploadedFilesStore} from 'presentation/components/UploadFiles/store/useUploadedFilesStore';
import {FeaturesEnum, OperationStateTypesEnum} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useGetProvidersListQuery} from 'presentation/pages/private/authorizations/queries/useGetProvidersListQuery';
import {usePatchAuthRequestMutation} from 'presentation/pages/private/authorizations/queries/usePatchAuthRequestMutation';
import {usePostProvidersListMutation} from 'presentation/pages/private/authorizations/queries/usePostProvidersListMutation';
import {useAuthorizationsStore} from 'presentation/pages/private/authorizations/store/authorizationsStore';
import {translations} from 'presentation/translations-new';
import {getUserInfoFromToken} from 'presentation/utils';
import {useCallback, useState} from 'react';
import {useForm, useWatch} from 'react-hook-form';
import {useLocationStore} from '../../store/locationStore';
import {FlowType} from '../../store/locationStore/constants';
import {useAccountDataStore} from '../../store/useAccountDataStateStore';
import {LIST_SIZE} from '../constants';
import {usePostAuthRequestMutation} from '../queries/usePostAuthRequestMutation';

const {warningSearch, warningEmptySearch, selectOneOption} =
	translations['pt-br'].privateFlow.requestAuthorizations.request.ProviderSelection;

export function useProviderSelection(onNextStep?: () => void) {
	const resetUploadedFilesState = useUploadedFilesStore(state => state.resetUploadedFiles);
	const resetExternalErrorMessage = useExternalErrorMessageStore(
		state => state.resetExternalErrorMessage,
	);
	const {resetProvidersList} = useAuthorizationsStore();
	const setNuControle = useAuthorizationsStore(state => state.setNuControle);
	const setHasProviderPreference = useAuthorizationsStore(
		state => state.setHasProviderPreference,
	);
	const setProviderObservation = useAuthorizationsStore(state => state.setProviderObservation);
	const setCdUf = useAuthorizationsStore(state => state.setCdUf);
	const cdUf = useAuthorizationsStore(state => state.cdUf);
	const providersList = useAuthorizationsStore(state => state.providersList);

	const methods = useForm({defaultValues: {hasProviderPreference: undefined}});
	const {control, getValues} = methods;

	const hasProviderPreference = useWatch({control, name: 'hasProviderPreference'});
	const radioValue = getValues('hasProviderPreference');

	const [observationState, setObservationState] = useState('');
	const [warningMessageState, setWarningMessageState] = useState('');
	const [errorRadioState, setErrorRadioState] = useState('');
	const [errorObservationState, setErrorObservationState] = useState(false);
	const [openDrawerState, setOpenDrawerState] = useState(false);
	const [showSnackbarState, setShowSnackbarState] = useState(false);
	const [selectedState, setSelectedState] = useState<Provider[]>([]);
	const [operationScheduleState, setOperationScheduleState] = useState(
		OperationStateTypesEnum.NONE,
	);

	const IS_POST_AUTH_REQUEST_LOADING =
		operationScheduleState === OperationStateTypesEnum.SUBMITTING;
	const IS_POST_AUTH_REQUEST_ERROR = operationScheduleState === OperationStateTypesEnum.ERROR;
	const IS_POST_AUTH_REQUEST_DONE = operationScheduleState === OperationStateTypesEnum.DONE;

	const cdPessoaC = getUserInfoFromToken().cdPessoa;
	const getLocation = useLocationStore(state => state.getLocation);
	const selectedLocation = getLocation(cdPessoaC, FlowType.AUTHORIZATION);
	const {cdUf: cdUfSelected} = selectedLocation.state;
	const nmCidade = selectedLocation.city;

	const {currentEmail, currentPhone} = usePersonalData();
	const {companyPortal} = useAccountDataStore(state => state.accountData);

	const {mutateAsync: postAuthRequestMutation} = usePostAuthRequestMutation();

	const {isEnabled: isAutomationFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_AUTOMACAO_AUTOSOL,
	);

	const {
		refetch: getProvidersList,
		isFetching: isGetProvidersFetching,
		data: getProvidersData,
	} = useGetProvidersListQuery({
		cdUf: cdUfSelected,
		nmCidade,
		page: 1,
		size: LIST_SIZE,
	});

	const {mutateAsync: providersListMutation} = usePostProvidersListMutation();
	const {mutateAsync: patchAuthRequest} = usePatchAuthRequestMutation();

	const handleCloseModalError = useCallback(() => {
		setOperationScheduleState(OperationStateTypesEnum.NONE);
	}, []);

	const handleValidation = async (): Promise<boolean> => {
		setErrorRadioState('');
		setErrorObservationState(false);

		const trimmedObservation = observationState.trim();

		const isSelectProviderRequired =
			radioValue === true && providersList.length <= 0 && !trimmedObservation;
		const isObservationRequired =
			trimmedObservation.length > 0 &&
			(trimmedObservation.length < 10 || trimmedObservation.length > 2000);
		const isEmptyProviderListResponse =
			radioValue &&
			getProvidersData &&
			getProvidersData.totalElements <= 0 &&
			!trimmedObservation;

		let isValid = true;

		if (isAutomationFlagEnabled && radioValue === undefined) {
			setErrorRadioState(selectOneOption);
			isValid = false;
		}

		if (isAutomationFlagEnabled && isSelectProviderRequired) {
			setWarningMessageState(warningSearch);
			isValid = false;
		}

		if (isAutomationFlagEnabled && isEmptyProviderListResponse) {
			setWarningMessageState(warningEmptySearch);
			setErrorObservationState(true);
			isValid = false;
		}

		if (isAutomationFlagEnabled && isObservationRequired) {
			setErrorObservationState(true);
			isValid = false;
		}

		return isValid;
	};

	const authRequestParams = {
		cdUf: cdUfSelected,
		nmCidade,
		dsEmail: currentEmail,
		nuFone: currentPhone,
		nomeOperador: companyPortal?.operatorName,
		emailOperador: companyPortal?.operatorEmail,
		documentoOperador: companyPortal?.operatorDocument,
	};

	const handleCreateNewRequest = async () => {
		const isValid = await handleValidation();
		if (!isValid) return;

		resetExternalErrorMessage();

		try {
			setOperationScheduleState(OperationStateTypesEnum.SUBMITTING);
			const response = await postAuthRequestMutation({...authRequestParams}).catch(err => {
				throw new Error('In postAuthRequestMutation:', err);
			});

			const nuControle = response?.nuControle?.toString();
			if (!nuControle) {
				throw new Error('nuControle is undefined');
			}

			setNuControle(nuControle);
			setCdUf(cdUf);

			if (observationState) {
				await patchAuthRequest({
					nuControle,
					observacoes: observationState,
				}).catch(err => {
					throw new Error('In patchAuthRequest (observation):', err);
				});
			}

			if (providersList.length > 0 && radioValue !== false) {
				await providersListMutation({
					nuControle: Number(nuControle),
					prestadores: providersList,
				}).catch(err => {
					throw new Error('In providersListMutation:', err);
				});
			}

			setOperationScheduleState(OperationStateTypesEnum.DONE);
			if (isAutomationFlagEnabled) {
				resetUploadedFilesState();
			}

			if (radioValue === false) resetProvidersList();

			setHasProviderPreference(radioValue || false);
			setProviderObservation(observationState.trim());

			onNextStep?.();
		} catch (error) {
			console.error('Error in handleCreateNewRequest:', error);
			setOperationScheduleState(OperationStateTypesEnum.ERROR);
		}
	};

	const onOpenDrawer = async () => {
		if (isGetProvidersFetching) return;

		const response = await getProvidersList();

		if (response.data?.content.length === 0) {
			setWarningMessageState(warningEmptySearch);
			return;
		}

		if (warningMessageState) {
			setWarningMessageState('');
			setErrorObservationState(false);
		}
		setSelectedState(providersList);
		setOpenDrawerState(true);
	};

	const onCloseDrawer = () => setOpenDrawerState(false);

	const resetFieldsOnPreferenceChange = () => {
		setErrorRadioState('');
		setObservationState('');
		setWarningMessageState('');
		setErrorObservationState(false);
		setHasProviderPreference(false);
		setProviderObservation('');
	};

	const handleSetObservationState = (value: string) => {
		setObservationState(value);
		if (errorObservationState && value.length > 0) {
			setErrorObservationState(false);
		}
	};

	return {
		methods,
		hasProviderPreference,
		observationState,
		setObservationState,
		errorObservationState,
		errorRadioState,
		radioValue,
		isGetProvidersFetching,
		providersList,
		onOpenDrawer,
		openDrawerState,
		onCloseDrawer,
		warningMessageState,
		showSnackbarState,
		setShowSnackbarState,
		selectedState,
		setSelectedState,
		resetFieldsOnPreferenceChange,
		control,
		IS_POST_AUTH_REQUEST_DONE,
		IS_POST_AUTH_REQUEST_ERROR,
		IS_POST_AUTH_REQUEST_LOADING,
		handleCloseModalError,
		handleSetObservationState,
		handleCreateNewRequest,
	};
}
